import React, { useState, useRef, useCallback } from 'react';
import { Hari, HariCorner } from '../types';

interface HariEditDialogProps {
  hari: Hari;
  horizontal: number;
  vertical: number;
  panelThick: number;
  onConfirm: (hari: Hari) => void;
  onDelete: (id: string) => void;
  onCancel: () => void;
}

const CORNER_LABELS: { value: HariCorner; label: string }[] = [
  { value: 'topLeft',     label: '좌상' },
  { value: 'topRight',    label: '우상' },
  { value: 'bottomLeft',  label: '좌하' },
  { value: 'bottomRight', label: '우하' },
];

const WALL_LABELS: { value: 'left'|'right'|'top'|'bottom'; label: string }[] = [
  { value: 'left',   label: '좌측벽' },
  { value: 'right',  label: '우측벽' },
  { value: 'top',    label: '상단벽' },
  { value: 'bottom', label: '하단벽' },
];

const DIALOG_W = 320;

const HariEditDialog: React.FC<HariEditDialogProps> = ({
  hari, horizontal, vertical, panelThick, onConfirm, onDelete, onCancel,
}) => {
  const [width, setWidth] = useState(hari.width);
  const [height, setHeight] = useState(hari.height);
  const [corner, setCorner] = useState<HariCorner>(hari.corner ?? 'topLeft');
  const [wallSide, setWallSide] = useState<'left'|'right'|'top'|'bottom'>(hari.wallSide ?? 'left');
  const [position, setPosition] = useState(hari.position ?? 0);

  const [dialogPos, setDialogPos] = useState({ x: 24, y: window.innerHeight - 420 - 80 });
  const dragging = useRef(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, px: dialogPos.x, py: dialogPos.y };
    e.preventDefault();
  }, [dialogPos]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    setDialogPos({
      x: dragStart.current.px + (e.clientX - dragStart.current.mx),
      y: dragStart.current.py + (e.clientY - dragStart.current.my),
    });
  }, []);

  const onMouseUp = useCallback(() => { dragging.current = false; }, []);

  const handleConfirm = () => {
    const pt = panelThick;
    if (hari.hariType === 'corner') {
      const maxW = (horizontal - pt * 2) / 2;
      const maxH = (vertical - pt * 2) / 2;
      if (width <= 0 || width > maxW) { alert(`가로는 1~${Math.floor(maxW)}mm 사이여야 합니다.`); return; }
      if (height <= 0 || height > maxH) { alert(`세로는 1~${Math.floor(maxH)}mm 사이여야 합니다.`); return; }
      onConfirm({ ...hari, corner, width, height });
    } else {
      const isHoriz = wallSide === 'top' || wallSide === 'bottom';
      const wallLen = isHoriz ? horizontal - pt : vertical - pt;
      if (position < 0 || position + width > wallLen) {
        alert('하리 위치가 벽을 벗어납니다.'); return;
      }
      onConfirm({ ...hari, wallSide, position, width, height });
    }
  };

  return (
    <div
      style={{
        position: 'fixed', left: dialogPos.x, top: dialogPos.y, width: DIALOG_W, zIndex: 1000,
        background: '#fff', borderRadius: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.16)', overflow: 'hidden', userSelect: 'none',
      }}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
    >
      <div onMouseDown={onMouseDown} style={{
        background: '#fff', color: '#191F28', padding: '16px 20px 8px',
        borderRadius: 0, cursor: 'grab',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 'bold', fontSize: 14 }}>
          하리 수정 ({hari.hariType === 'corner' ? '코너형' : '중간형'})
        </span>
        <button onClick={onCancel} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#F2F4F6', color: '#4E5968', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>✕</button>
      </div>

      <div style={{ padding: 16 }}>

        {/* 코너형 - 코너 위치 선택 */}
        {hari.hariType === 'corner' && (
          <div style={fieldStyle}>
            <label style={dlabelStyle}>코너 위치</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {CORNER_LABELS.map(cl => (
                <button key={cl.value} onClick={() => setCorner(cl.value)} style={{
                  flex: 1, padding: '7px 4px', cursor: 'pointer', borderRadius: 6, fontSize: 12,
                  border: corner === cl.value ? '2px solid #3182F6' : '1px solid #E5E8EB',
                  background: corner === cl.value ? '#E8F3FF' : '#fff',
                  color: corner === cl.value ? '#263238' : '#666',
                  fontWeight: corner === cl.value ? 'bold' : 'normal',
                }}>{cl.label}</button>
              ))}
            </div>
          </div>
        )}

        {/* 중간형 - 벽면 + 위치 */}
        {hari.hariType === 'middle' && (
          <>
            <div style={fieldStyle}>
              <label style={dlabelStyle}>벽면</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {WALL_LABELS.map(wl => (
                  <button key={wl.value} onClick={() => setWallSide(wl.value)} style={{
                    flex: 1, padding: '7px 4px', cursor: 'pointer', borderRadius: 6, fontSize: 11,
                    border: wallSide === wl.value ? '2px solid #3182F6' : '1px solid #E5E8EB',
                    background: wallSide === wl.value ? '#E8F3FF' : '#fff',
                    color: wallSide === wl.value ? '#263238' : '#666',
                    fontWeight: wallSide === wl.value ? 'bold' : 'normal',
                  }}>{wl.label}</button>
                ))}
              </div>
            </div>
            <div style={fieldStyle}>
              <label style={dlabelStyle}>위치 (벽 시작에서 mm)</label>
              <input type="number" value={position} onChange={e => setPosition(Number(e.target.value))}
                style={dinputStyle} step={10} />
            </div>
          </>
        )}

        {/* 가로 크기 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>
            {hari.hariType === 'corner' ? '가로 (수평벽 차지 길이, mm)' : '가로 (벽 길이 방향, mm)'}
          </label>
          <input type="number" value={width} onChange={e => setWidth(Number(e.target.value))}
            style={dinputStyle} step={10} min={1} />
        </div>

        {/* 세로 크기 */}
        <div style={fieldStyle}>
          <label style={dlabelStyle}>
            {hari.hariType === 'corner' ? '세로 (수직벽 차지 길이, mm)' : '세로 (벽 안쪽 돌출, mm)'}
          </label>
          <input type="number" value={height} onChange={e => setHeight(Number(e.target.value))}
            style={dinputStyle} step={10} min={1} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
          <button onClick={() => { if (window.confirm('하리를 삭제하시겠습니까?')) onDelete(hari.id); }}
            style={deleteBtnStyle}>삭제</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} style={cancelBtnStyle}>취소</button>
            <button onClick={handleConfirm} style={confirmBtnStyle}>적용</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const fieldStyle: React.CSSProperties = { marginBottom: 12 };
const dlabelStyle: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 'bold', color: '#555' };
const dinputStyle: React.CSSProperties = { width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8, boxSizing: 'border-box' };
const deleteBtnStyle: React.CSSProperties = { height: 38, padding: '0 16px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#FFEEF0', color: '#F04452', cursor: 'pointer' };
const cancelBtnStyle: React.CSSProperties = { height: 38, padding: '0 16px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#F2F4F6', color: '#333D4B', cursor: 'pointer' };
const confirmBtnStyle: React.CSSProperties = { height: 38, padding: '0 18px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 10, background: '#3182F6', color: '#fff', cursor: 'pointer' };

export default HariEditDialog;
